import { useMemo, useState } from 'react';
import { BsFillPatchQuestionFill } from 'react-icons/bs';

import EmptyState from '../../../../components/emptyState';
import { Drawer } from '../../../../components/Drawer';

import close from '../../../../assets/svg-icons/closeBrand.svg';
import arrow from '../../../../assets/svg-icons/blueArrow.svg';
import editIcon from '../../../../assets/svg-icons/edit.svg';
import deleteIcon from '../../../../assets/svg-icons/deleteRed.svg';

export default function SiteConnectionsTab({
  filtered,
  selectedIds,
  toggleAll,
  toggleOne,
  onOpenCreate,
  onRefresh,
  onRequestDelete,
  onOpenEdit,
  vpnServices,
  ikePolicies,
  ipsecPolicies,
  endpointGroupOptions,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [manageModalIndex, setManageModalIndex] = useState(null);

  const rows = useMemo(() => (Array.isArray(filtered) ? filtered : []), [filtered]);

  const rowIds = rows.map((row) => row.id || row.uuid).filter(Boolean);

  const allRowsSelected =
    rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));

  const closeManageModal = () => setManageModalIndex(null);

  const getByIdOrName = (list, id) => {
    if (!id) return '—';

    const found = Array.isArray(list)
      ? list.find((item) => (item.id || item.uuid) === id)
      : null;

    return found?.name || id || '—';
  };

  const getVpnServiceName = (id) => getByIdOrName(vpnServices, id);

  const getIkePolicyName = (id) => getByIdOrName(ikePolicies, id);

  const getIpsecPolicyName = (id) => getByIdOrName(ipsecPolicies, id);

  const getEndpointGroupName = (id) => {
    if (!id) return '—';

    const found = Array.isArray(endpointGroupOptions)
      ? endpointGroupOptions.find((item) => item.value === id)
      : null;

    return found?.label || id || '—';
  };

  const getPeerCidrs = (row) => {
    if (Array.isArray(row?.peer_cidrs)) {
      return row.peer_cidrs.join(', ') || '—';
    }

    return row?.peer_cidrs || '—';
  };

  const getAdminState = (row) => {
    if (row?.admin_state_up === true) return 'UP';
    if (row?.admin_state_up === false) return 'DOWN';

    return row?.admin_state || '—';
  };

  return (
    <>
      <table className="w-full text-sm text-left overflow-hidden">
        <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] text-sm font-semibold">
          <tr>
            <th className="px-6 py-4 whitespace-nowrap">
              <input
                type="checkbox"
                checked={allRowsSelected}
                onChange={toggleAll}
              />
            </th>

            <th className="px-6 py-4 whitespace-nowrap">Name</th>
            <th className="px-6 py-4 whitespace-nowrap">Description</th>
            <th className="px-6 py-4 whitespace-nowrap">VPN Service</th>
            <th className="px-6 py-4 whitespace-nowrap">IKE Policy</th>
            <th className="px-6 py-4 whitespace-nowrap">IPSec Policy</th>
            <th className="px-6 py-4 whitespace-nowrap">Status</th>
            <th className="px-6 py-4 whitespace-nowrap">Actions</th>
          </tr>
        </thead>

        <tbody className="bg-white dark:bg-slate-800 text-black/50 dark:text-white/60 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] text-sm">
          {rows.length === 0 ? (
            <tr>
              <td className="px-6 py-10" colSpan={8}>
                <EmptyState
                  title="No IPSec site connections yet"
                  description="Create site connections to connect your local and remote networks."
                  primaryAction={{ label: 'Add Site Connection', onClick: onOpenCreate }}
                  secondaryAction={{ label: 'Refresh', onClick: onRefresh }}
                />
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const id = row.id || row.uuid;

              return (
                <tr
                  key={id}
                  className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                >
                  <td className="text-start py-4 px-6">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(id)}
                      onChange={() => toggleOne(id)}
                    />
                  </td>

                  <td className="text-start py-4 px-6">
                    <button
                      type="button"
                      className="text-primaryBlue hover:underline"
                      onClick={() => {
                        setSelected(row);
                        setDetailsOpen(true);
                      }}
                    >
                      {row.name || '—'}
                    </button>
                  </td>

                  <td className="text-start py-4 px-6">
                    {row.description || '—'}
                  </td>

                  <td className="text-start py-4 px-6">
                    {row.vpnservice_name || getVpnServiceName(row.vpnservice_id)}
                  </td>

                  <td className="text-start py-4 px-6">
                    {row.ikepolicy_name || getIkePolicyName(row.ikepolicy_id)}
                  </td>

                  <td className="text-start py-4 px-6">
                    {row.ipsecpolicy_name || getIpsecPolicyName(row.ipsecpolicy_id)}
                  </td>

                  <td className="text-start py-4 px-6">
                    {row.status || '—'}
                  </td>

                  <td className="text-start py-4 px-6 relative">
                    <button
                      className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                      onClick={() => setManageModalIndex(index)}
                      type="button"
                    >
                      Manage IPSec Connection
                    </button>

                    {manageModalIndex === index && (
                      <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
                        <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
                          <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
                            <span className="dark:text-white text-black font-medium">
                              Manage "{row.name}"
                            </span>

                            <img
                              src={close}
                              onClick={closeManageModal}
                              className="h-5 cursor-pointer"
                              alt=""
                            />
                          </div>

                          <div className="p-4 grid gap-1">
                            <div
                              className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all text-black dark:text-white font-medium cursor-pointer"
                             onClick={() => {
                              onOpenEdit(row);
                              closeManageModal();
                            }}
                            >
                              <div className="flex items-center gap-2">
                                <img src={editIcon} alt="" className="h-4" />
                                <span>Edit IPSec Connection</span>
                              </div>

                              <img src={arrow} alt="" />
                            </div>

                            <div
                              className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all text-red-500 font-medium cursor-pointer"
                              onClick={() => {
                                onRequestDelete(
                                  id,
                                  row.name || id,
                                  'Delete IPSec Site Connection',
                                );
                                closeManageModal();
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <img src={deleteIcon} alt="" className="h-4" />
                                <span>Delete IPSec Connection</span>
                              </div>

                              <img src={arrow} alt="" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <Drawer
        open={detailsOpen}
        title={selected?.name || 'IPSec Connection'}
        onClose={() => {
          setDetailsOpen(false);
          setSelected(null);
        }}
        width={650}
      >
        {selected ? (
          <div className="grid gap-6">
            <div className="flex items-start justify-between gap-3">
              <div className="text-xl font-semibold">{selected.name}</div>

              <button
                type="button"
                className="text-xs rounded-md px-3 py-2 cursor-pointer bg-white dark:bg-slate-800 border border-[#c4c4c460] hover:bg-brandBlue/10"
                onClick={() => onOpenEdit(selected)}
              >
                Edit IPSec Connection
              </button>
            </div>

            <div>
              <p className="text-lg font-semibold mb-3">
                IPSec Site Connection Details
              </p>

              <div className="border dark:border-[#c4c4c432] rounded-md overflow-hidden">
                {[
                  ['Name', selected.name],
                  ['Description', selected.description || '—'],
                  ['ID', selected.id || selected.uuid],
                  ['Project ID', selected.project_id],
                  [
                    'VPN Service',
                    selected.vpnservice_name || getVpnServiceName(selected.vpnservice_id),
                  ],
                  [
                    'IKE Policy',
                    selected.ikepolicy_name || getIkePolicyName(selected.ikepolicy_id),
                  ],
                  [
                    'IPSec Policy',
                    selected.ipsecpolicy_name ||
                      getIpsecPolicyName(selected.ipsecpolicy_id),
                  ],
                  ['Peer Address', selected.peer_address],
                  ['Peer ID', selected.peer_id],
                  ['Peer CIDRs', getPeerCidrs(selected)],
                  [
                    'Local Endpoint Group',
                    getEndpointGroupName(selected.local_ep_group_id),
                  ],
                  [
                    'Peer Endpoint Group',
                    getEndpointGroupName(selected.peer_ep_group_id),
                  ],
                  ['MTU', selected.mtu],
                  ['Initiator', selected.initiator],
                  [
                    'DPD Action',
                    selected?.dpd?.action ?? selected?.dpd_action ?? '—',
                  ],
                  [
                    'DPD Interval',
                    selected?.dpd?.interval ?? selected?.dpd_interval ?? '—',
                  ],
                  [
                    'DPD Timeout',
                    selected?.dpd?.timeout ?? selected?.dpd_timeout ?? '—',
                  ],
                  ['Admin State', getAdminState(selected)],
                  ['Status', selected.status],
                ].map(([key, value]) => {
                  const fieldDescriptions = {
                    Name: 'The name of the IPSec site connection.',
                    Description: 'A description for the IPSec site connection.',
                    'VPN Service': 'The VPN service associated with this connection.',
                    'IKE Policy': 'The IKE policy associated with this connection.',
                    'IPSec Policy': 'The IPSec policy associated with this connection.',
                    'Peer Address': 'The peer gateway public IPv4 or IPv6 address or FQDN.',
                    'Peer ID':
                      'The peer router identity for authentication. Can be IPv4/IPv6 address, e-mail, key ID or FQDN.',
                    'Peer CIDRs':
                      'Remote peer subnet address(es) with mask(s) in CIDR format.',
                    'Local Endpoint Group':
                      'Local endpoint group ID with encapsulation mode as tunnel.',
                    'Peer Endpoint Group':
                      'Peer endpoint group ID with encapsulation mode as tunnel.',
                    MTU:
                      'The maximum transmission unit value to be used on the VPN link.',
                    Initiator:
                      'The initiator of the VPN connection. Default is bi-directional.',
                    'DPD Action':
                      'Dead Peer Detection action. Default is restart.',
                    'DPD Interval':
                      'Dead Peer Detection interval in seconds. Default is 30.',
                    'DPD Timeout':
                      'Dead Peer Detection timeout in seconds. Default is 120.',
                    'Admin State':
                      'The administrative state of this IPSec site connection.',
                    Status: 'The current provisioning status of this IPSec site connection.',
                  };

                  return (
                    <div
                      key={key}
                      className="grid grid-cols-3 gap-3 px-4 py-3 border-b last:border-b-0 dark:border-[#c4c4c432]"
                    >
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center">
                        {key}

                        {fieldDescriptions[key] && (
                          <BsFillPatchQuestionFill
                            className="text-xs text-primaryBlue ml-1 cursor-pointer"
                            title={fieldDescriptions[key]}
                          />
                        )}
                      </div>

                      <div className="col-span-2 text-sm text-gray-900 dark:text-white">
                        {value ?? '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm opacity-70">No connection selected.</div>
        )}
      </Drawer>
    </>
  );
}